import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, Download, AlertCircle, UserCircle, RotateCcw, Image as ImageIcon, ZoomIn, Move } from 'lucide-react';

type ShapeType = 'circle' | 'rounded-square' | 'square';
type BackgroundType = 'transparent' | 'white' | 'custom';

interface SizePreset {
  label: string;
  width: number;
  height: number;
}

const SIZE_PRESETS: SizePreset[] = [
  { label: 'Custom', width: 400, height: 400 },
  { label: 'Instagram', width: 320, height: 320 },
  { label: 'Twitter/X', width: 400, height: 400 },
  { label: 'LinkedIn', width: 400, height: 400 },
  { label: 'Facebook', width: 170, height: 170 },
  { label: 'YouTube', width: 800, height: 800 },
  { label: 'Discord', width: 128, height: 128 },
  { label: 'Slack', width: 512, height: 512 },
];

const SHAPES: { value: ShapeType; label: string }[] = [
  { value: 'circle', label: 'Circle' },
  { value: 'rounded-square', label: 'Rounded Square' },
  { value: 'square', label: 'Square' },
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ProfilePictureMaker: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [shape, setShape] = useState<ShapeType>('circle');
  const [sizePreset, setSizePreset] = useState<SizePreset>(SIZE_PRESETS[0]);
  const [customSize, setCustomSize] = useState(400);
  const [bgType, setBgType] = useState<BackgroundType>('transparent');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [borderWidth, setBorderWidth] = useState(0);
  const [borderColor, setBorderColor] = useState('#000000');
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [imgElement, setImgElement] = useState<HTMLImageElement | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ w: number; h: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const actualSizeCanvasRef = useRef<HTMLCanvasElement>(null);

  const getOutputSize = useCallback(() => {
    if (sizePreset.label === 'Custom') return customSize;
    return sizePreset.width;
  }, [sizePreset, customSize]);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    setFile(f);
    setError(null);
    setZoom(1);
    setPanX(0);
    setPanY(0);
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      const img = new window.Image();
      img.onload = () => {
        setImgElement(img);
        setImageDimensions({ w: img.naturalWidth, h: img.naturalHeight });
      };
      img.onerror = () => setError('Failed to load image.');
      img.src = url;
    };
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const drawProfile = useCallback((canvas: HTMLCanvasElement, img: HTMLImageElement, size: number) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = size;
    canvas.height = size;

    // Background
    if (bgType === 'transparent') {
      ctx.clearRect(0, 0, size, size);
    } else {
      ctx.fillStyle = bgType === 'white' ? '#ffffff' : bgColor;
      ctx.fillRect(0, 0, size, size);
    }

    // Clip shape
    ctx.save();
    const bw = borderWidth;
    const innerSize = size - bw * 2;

    if (shape === 'circle') {
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, innerSize / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
    } else if (shape === 'rounded-square') {
      const r = innerSize * 0.15;
      const x = bw, y = bw;
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + innerSize - r, y);
      ctx.quadraticCurveTo(x + innerSize, y, x + innerSize, y + r);
      ctx.lineTo(x + innerSize, y + innerSize - r);
      ctx.quadraticCurveTo(x + innerSize, y + innerSize, x + innerSize - r, y + innerSize);
      ctx.lineTo(x + r, y + innerSize);
      ctx.quadraticCurveTo(x, y + innerSize, x, y + innerSize - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
      ctx.clip();
    } else {
      ctx.beginPath();
      ctx.rect(bw, bw, innerSize, innerSize);
      ctx.clip();
    }

    // Draw image with zoom and pan
    const imgAspect = img.naturalWidth / img.naturalHeight;
    let drawW: number, drawH: number;
    if (imgAspect > 1) {
      drawH = innerSize * zoom;
      drawW = drawH * imgAspect;
    } else {
      drawW = innerSize * zoom;
      drawH = drawW / imgAspect;
    }

    const drawX = bw + (innerSize - drawW) / 2 + panX * (innerSize / 300);
    const drawY = bw + (innerSize - drawH) / 2 + panY * (innerSize / 300);

    ctx.drawImage(img, drawX, drawY, drawW, drawH);
    ctx.restore();

    // Draw border
    if (bw > 0) {
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = bw;
      if (shape === 'circle') {
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, (size - bw) / 2, 0, Math.PI * 2);
        ctx.stroke();
      } else if (shape === 'rounded-square') {
        const r = (size - bw) * 0.15;
        const x = bw / 2, y = bw / 2;
        const s = size - bw;
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + s - r, y);
        ctx.quadraticCurveTo(x + s, y, x + s, y + r);
        ctx.lineTo(x + s, y + s - r);
        ctx.quadraticCurveTo(x + s, y + s, x + s - r, y + s);
        ctx.lineTo(x + r, y + s);
        ctx.quadraticCurveTo(x, y + s, x, y + s - r);
        ctx.lineTo(x, y + r);
        ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.closePath();
        ctx.stroke();
      } else {
        ctx.strokeRect(bw / 2, bw / 2, size - bw, size - bw);
      }
    }
  }, [shape, bgType, bgColor, borderWidth, borderColor, zoom, panX, panY]);

  useEffect(() => {
    if (!imgElement) return;
    const size = getOutputSize();
    // Preview at larger size for visibility
    const previewSize = Math.max(300, Math.min(400, size));
    if (previewCanvasRef.current) {
      drawProfile(previewCanvasRef.current, imgElement, previewSize);
    }
    if (actualSizeCanvasRef.current) {
      drawProfile(actualSizeCanvasRef.current, imgElement, size);
    }
  }, [imgElement, drawProfile, getOutputSize]);

  // Pan handling on preview canvas
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - panX, y: e.clientY - panY });
  }, [panX, panY]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !dragStart) return;
    setPanX(e.clientX - dragStart.x);
    setPanY(e.clientY - dragStart.y);
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  const handleDownload = useCallback(() => {
    if (!imgElement) return;
    const size = getOutputSize();
    const canvas = document.createElement('canvas');
    drawProfile(canvas, imgElement, size);
    const dataUrl = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    const baseName = file?.name?.replace(/\.[^.]+$/, '') || 'profile';
    link.download = `${baseName}-${size}x${size}.png`;
    link.href = dataUrl;
    link.click();
  }, [imgElement, drawProfile, getOutputSize, file]);

  const handleReset = useCallback(() => {
    setFile(null);
    setImgElement(null);
    setImageDimensions(null);
    setShape('circle');
    setSizePreset(SIZE_PRESETS[0]);
    setCustomSize(400);
    setBgType('transparent');
    setBorderWidth(0);
    setZoom(1);
    setPanX(0);
    setPanY(0);
    setError(null);
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <UserCircle className="w-6 h-6 text-pink-600" />
          <h1 className="text-2xl font-bold text-gray-900">Profile Picture Maker</h1>
        </div>
        <p className="text-gray-600">Create perfectly sized profile pictures for any social platform.</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {!file ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center cursor-pointer hover:border-pink-400 hover:bg-pink-50/50 transition-colors"
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-1">Drop an image here or click to upload</p>
          <p className="text-sm text-gray-500">Supports PNG, JPG, WebP, GIF</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files)}
          />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Controls */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-gray-900">Settings</h2>
                <button onClick={handleReset} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
                  <RotateCcw className="w-4 h-4" /> Reset
                </button>
              </div>

              {/* Shape */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Shape</label>
                <div className="flex gap-2">
                  {SHAPES.map((s) => (
                    <button
                      key={s.value}
                      onClick={() => setShape(s.value)}
                      className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                        shape === s.value
                          ? 'border-pink-500 bg-pink-50 text-pink-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Size presets */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Size Preset</label>
                <div className="flex flex-wrap gap-2">
                  {SIZE_PRESETS.map((p) => (
                    <button
                      key={p.label}
                      onClick={() => setSizePreset(p)}
                      className={`px-3 py-1.5 rounded-lg border text-xs transition-colors ${
                        sizePreset.label === p.label
                          ? 'border-pink-500 bg-pink-50 text-pink-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      <div className="font-medium">{p.label}</div>
                      <div className="text-gray-400">{p.width}x{p.height}</div>
                    </button>
                  ))}
                </div>
                {sizePreset.label === 'Custom' && (
                  <div className="mt-2">
                    <label className="block text-xs text-gray-600 mb-1">Custom Size: {customSize}px</label>
                    <input
                      type="range"
                      min={64}
                      max={1024}
                      value={customSize}
                      onChange={(e) => setCustomSize(Number(e.target.value))}
                      className="w-full accent-pink-600"
                    />
                  </div>
                )}
              </div>

              {/* Background */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Background</label>
                <div className="flex gap-2 items-center">
                  {(['transparent', 'white', 'custom'] as BackgroundType[]).map((b) => (
                    <button
                      key={b}
                      onClick={() => setBgType(b)}
                      className={`px-3 py-2 rounded-lg border text-sm capitalize transition-colors ${
                        bgType === b
                          ? 'border-pink-500 bg-pink-50 text-pink-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      {b}
                    </button>
                  ))}
                  {bgType === 'custom' && (
                    <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="w-10 h-10 rounded border border-gray-300 cursor-pointer" />
                  )}
                </div>
              </div>

              {/* Border */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-700">Border Width</label>
                  <span className="text-sm font-mono text-pink-600">{borderWidth}px</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={20}
                  value={borderWidth}
                  onChange={(e) => setBorderWidth(Number(e.target.value))}
                  className="w-full accent-pink-600"
                />
                {borderWidth > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    <label className="text-xs text-gray-600">Border Color</label>
                    <input type="color" value={borderColor} onChange={(e) => setBorderColor(e.target.value)} className="w-8 h-8 rounded border border-gray-300 cursor-pointer" />
                  </div>
                )}
              </div>

              {/* Zoom */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <ZoomIn className="w-4 h-4" /> Zoom
                  </label>
                  <span className="text-sm font-mono text-pink-600">{zoom.toFixed(1)}x</span>
                </div>
                <input
                  type="range"
                  min={0.5}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="w-full accent-pink-600"
                />
              </div>

              {/* Pan info */}
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Move className="w-3 h-3" />
                <span>Click and drag the preview to reposition the image.</span>
              </div>
            </div>

            {/* Preview */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Preview (drag to pan)
                </h3>
                <div
                  className="border border-gray-200 rounded-lg overflow-hidden bg-gray-100 p-6 flex justify-center items-center"
                  style={{ backgroundImage: 'linear-gradient(45deg, #e5e5e5 25%, transparent 25%), linear-gradient(-45deg, #e5e5e5 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e5e5 75%), linear-gradient(-45deg, transparent 75%, #e5e5e5 75%)', backgroundSize: '20px 20px', backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px' }}
                >
                  <canvas
                    ref={previewCanvasRef}
                    className="cursor-move"
                    style={{ maxWidth: '100%', maxHeight: '400px' }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  />
                </div>
              </div>

              {/* Actual size preview */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Actual Size ({getOutputSize()}x{getOutputSize()}px)
                </h3>
                <div className="border border-gray-200 rounded-lg overflow-auto bg-gray-100 p-4 max-h-[300px]">
                  <canvas ref={actualSizeCanvasRef} />
                </div>
              </div>
            </div>
          </div>

          {/* Image info */}
          {imageDimensions && file && (
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1"><ImageIcon className="w-4 h-4" /> Original: {imageDimensions.w} x {imageDimensions.h}px</span>
              <span>{formatFileSize(file.size)}</span>
              <span>Output: {getOutputSize()}x{getOutputSize()}px PNG</span>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-6 py-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors font-medium"
            >
              <Download className="w-5 h-5" /> Download Profile Picture
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePictureMaker;
